import React from 'react';
import { groupBy, sortBy } from 'lodash';
import moment from 'moment';
import { Dialog, Classes } from '@blueprintjs/core';
import {
  faBan,
  faBell,
  faCircle,
  faCircleNotch,
  faClock,
  faCommentSlash,
  faHourglass,
  faMoon
} from '@fortawesome/pro-solid-svg-icons';

import Icon from '../../../atoms/icon';
import { Tabs, Tab } from '../../../molecules/tab';
import Button from '../../../molecules/button';
import Card, { CardBlock } from '../../card';
import PatientEvents from '../../patient/events';
import Alert from '../../alert';
import Callout from '../../callout';
import ErrorCallout from '../../callout/error-callout';

import { useApps } from '../../../../contexts/apps';
import { useTerms } from '../../../../contexts/terms';
import { useTriage } from '../../../../contexts/triage';

import { IAlert } from '../../../../libs/alert';
import { IPatient } from '../../../../libs/patient';

import { AddressTypeString } from './types';

interface RecentIssuesProps {
  addressScope: 'alert' | 'patient';
  addressType: 'snooze' | 'resolve';
  alertIds: number[];
  patient: IPatient;
  selectedAlertIds: number[];
  setAlertIds: (ids: number[]) => void;
}

const RecentIssues: React.FC<RecentIssuesProps> = ({
  addressScope,
  addressType,
  alertIds,
  patient,
  selectedAlertIds,
  setAlertIds
}) => {
  const { data } = useTriage();
  const { getAppName } = useApps();
  const alerts = alertIds
    .map((id) => data.alerts[id])
    .filter((alert) => !alert.isRead);
  if (!alerts || !alerts.length) {
    return (
      <Card>
        <CardBlock>
          <h3>No Recent Alerts for {patient.firstName}.</h3>
        </CardBlock>
      </Card>
    );
  }

  const toggleAlertSelected = (alertId: number) => {
    let ids: number[] = [];
    if (selectedAlertIds) {
      if (selectedAlertIds.includes(alertId)) {
        ids = selectedAlertIds.filter((id) => id !== alertId);
      } else {
        ids = selectedAlertIds.concat(alertId);
      }
    } else {
      ids = ids.concat(alertId);
    }
    setAlertIds(ids);
  };

  const generateCallout = (): JSX.Element => {
    if (addressScope === 'patient') {
      return (
        <Callout intent="success" className="text-small">
          All Alerts below will be {addressType}d if {patient.firstName} is{' '}
          {addressType}d.
        </Callout>
      );
    } else {
      if (!selectedAlertIds || selectedAlertIds?.length <= 0) {
        return (
          <ErrorCallout className="my-3 text-small">
            Please select at least one Alert below to {addressType}.
          </ErrorCallout>
        );
      } else {
        return (
          <Callout intent="primary" className="text-small">
            Click Alerts below to select or de-select the ones you'd like to{' '}
            {addressType}.
          </Callout>
        );
      }
    }
  };

  const SortAlertsDescending = (alerts: IAlert[]): IAlert[] => {
    const _alerts: IAlert[] = sortBy(alerts, (alert) => {
      const time = moment(alert.createdAt);
      return -time.valueOf();
    });
    return _alerts;
  };
  const generateAlerts = (alerts: IAlert[]): JSX.Element[] => {
    // Group alerts by Program
    const alertsByApp: any = groupBy(alerts, 'app');
    // Get keys of Apps associated to alerts
    const appsWithAlerts = Object.keys(alertsByApp);
    // Start component array
    const alertComponents: JSX.Element[] = [];
    appsWithAlerts.map((app) => {
      // Create a header for each app
      alertComponents.push(
        <li key={app} className="uppercase text-small text-gray2 mt-8">
          {getAppName(app)} Alerts
        </li>
      );
      // Sort the app's alerts from newest to oldest as users normally review newest alerts
      const _alerts = SortAlertsDescending(alertsByApp[app]);
      _alerts.map((alert: IAlert) => {
        alertComponents.push(
          <li key={alert.id}>
            <Button
              bare
              variant="minimal"
              fill
              className="shadow-none bg-none bg-image-none btn-text-wide pa-0-important"
              onClick={toggleAlertSelected.bind(this, alert.id)}
            >
              <Alert
                alert={alert}
                patient={patient}
                inButton
                hideViewProgram
                isSelectedInButton={
                  selectedAlertIds && selectedAlertIds.includes(alert.id)
                }
              />
            </Button>
          </li>
        );
      });
    });
    return alertComponents;
  };

  return (
    <React.Fragment>
      {generateCallout()}
      <ul className="list-none pl-0">{generateAlerts(alerts)}</ul>
    </React.Fragment>
  );
};

interface EventSummaryProps {
  isAddressingPatient: boolean;
  addressType: AddressTypeString;
  patient: IPatient;
  alertIds: number[];
  selectedAlertIds: number[];
  setSelectedAlertIds: (ids: number[]) => void;
}

const EventSummary: React.FC<EventSummaryProps> = ({
  isAddressingPatient,
  addressType,
  patient,
  alertIds,
  selectedAlertIds,
  setSelectedAlertIds
}) => {
  const patientId = patient ? patient.id : null;
  const { terms } = useTerms();
  return (
    <div>
      {isAddressingPatient && (
        <Dialog
          isOpen
          autoFocus={false}
          enforceFocus={false}
          canOutsideClickClose={false}
          canEscapeKeyClose={false}
          usePortal={false}
          className="bg-white"
        >
          <div className={Classes.DIALOG_BODY}>
            {addressType === 'resolve' ? (
              <>
                <h3>Resolve {terms.patient.capitalized}</h3>
                <p>
                  Any <Icon icon={faMoon} className="text-brand" /> Snoozes,{' '}
                  <Icon icon={faBell} className="text-blue3" /> Alerts, will be
                  resolved.
                </p>
                <p>
                  <Icon icon={faCircle} className="text-intent-danger" /> Red or{' '}
                  <Icon icon={faCircle} className="text-intent-warning" />{' '}
                  Yellow statuses will turn{' '}
                  <Icon icon={faCircle} className="text-intent-success" />{' '}
                  Green.
                </p>
                <p>
                  <Icon icon={faCircleNotch} /> Less-Engaged,{' '}
                  <Icon icon={faCommentSlash} className="text-blue3" />{' '}
                  Unconsented, or <Icon icon={faBan} className="text-blue3" />{' '}
                  Opted-Out programs will <strong>not</strong> turn Green.
                </p>
              </>
            ) : (
              <>
                <h3>Snooze {terms.patient.capitalized}</h3>
                <p>
                  All recent open Alerts will be snoozed.{' '}
                  {terms.patient.pluralCapitalized} are snoozed until the date
                  chosen unless:
                </p>
                <ul>
                  <li>They trigger a new Alert</li>
                  <li>Their status changes</li>
                </ul>
              </>
            )}
          </div>
        </Dialog>
      )}
      <Tabs id="event-summary-tabs">
        <Tab
          id="recent-issues"
          title={
            <>
              <Icon icon={faClock} className="mr-3" /> Recent Alerts
            </>
          }
          panel={
            <RecentIssues
              addressScope={isAddressingPatient ? 'patient' : 'alert'}
              addressType={addressType}
              alertIds={alertIds}
              patient={patient}
              selectedAlertIds={selectedAlertIds}
              setAlertIds={setSelectedAlertIds}
            />
          }
          disabled={isAddressingPatient}
        />
        <Tab
          id="event-history"
          title={
            <>
              <Icon icon={faHourglass} className="mr-3" /> Event History
            </>
          }
          panel={<PatientEvents patientId={patientId} />}
          panelClassName="text-small"
          disabled={isAddressingPatient}
        />
      </Tabs>
    </div>
  );
};

export default EventSummary;
